import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import moment from 'moment'

import routes from '../../routers'
import Layout from '../../components/Layout'
import { ArrowBack } from '../../components/SvgIcon'
import {
  cancelCookbookOrder,
  cookbookError,
  getCookbookOrder,
  getCookbookOrderRequests,
  getCookbookOrders,
  ORDER_STATUS_TEXT,
  REQUEST_STATUS_TEXT,
} from '../../api/cookbook'
import { STEPS_ORDER_BOOK } from '../../utils/constants'

import '../PurchaseBook/styles.css'
import styles from './CookbookOrders.module.css'

const dollars = (n) => (n === null || n === undefined ? '' : `$${Number(n).toFixed(2)}`)
const date = (d) => (d ? moment(d).format('MMM D, YYYY') : '')

const STAGES = [
  { key: 'paid', label: 'Paid' },
  { key: 'submitted', label: 'At the printer' },
  { key: 'in_production', label: 'Printing' },
  { key: 'shipped', label: 'Shipped' },
  { key: 'delivered', label: 'Delivered' },
]

const TONE = {
  paid: 'busy',
  submitted: 'busy',
  in_production: 'busy',
  shipped: 'good',
  delivered: 'good',
  failed: 'warn',
  pending_payment: 'warn',
  refunded: 'quiet',
  cancelled: 'quiet',
}

const StatusPill = ({ status }) => (
  <span className={`${styles.pill} ${styles[`pill_${TONE[status] || 'quiet'}`]}`}>
    {ORDER_STATUS_TEXT[status] || status}
  </span>
)

const Progress = ({ status }) => {
  const reached = STAGES.findIndex((s) => s.key === status)
  if (reached === -1) return null
  return (
    <ol className={styles.progress}>
      {STAGES.map((s, i) => (
        <li
          key={s.key}
          className={`${i <= reached ? styles.done : ''} ${i === reached ? styles.now : ''}`}
        >
          <span className={styles.dot}>{i < reached ? '✓' : ''}</span>
          {s.label}
        </li>
      ))}
    </ol>
  )
}

const OrderDetail = ({ id }) => {
  const { search } = useLocation()
  const justPaid = new URLSearchParams(search).has('paid')
  const [order, setOrder] = useState(null)
  const [error, setError] = useState('')
  const [cancelling, setCancelling] = useState(false)
  const tries = useRef(0)
  const timer = useRef(null)

  const load = () =>
    getCookbookOrder(id)
      .then(({ data }) => {
        setOrder(data)
        // Just back from payment: the confirmation can take a few seconds
        if (data.status === 'pending_payment' && tries.current < 20) {
          tries.current += 1
          timer.current = setTimeout(load, 3000)
        }
      })
      .catch((e) => setError(cookbookError(e, 'We could not load this order.')))

  useEffect(() => {
    load()
    return () => clearTimeout(timer.current)
  }, [id])

  const cancel = () => {
    if (!window.confirm('Cancel this order and get a full refund?')) return
    setCancelling(true)
    cancelCookbookOrder(id)
      .then(({ data }) => setOrder(data))
      .catch((e) => {
        setError(cookbookError(e, 'We could not cancel this order.'))
        load()
      })
      .finally(() => setCancelling(false))
  }

  if (error && !order) return <p className="cbError">{error}</p>
  if (!order) return <p className="cbMuted">Loading…</p>

  const a = order.shipping_address || {}
  return (
    <section className={`cbSection ${styles.card}`}>
      {justPaid && order.status !== 'pending_payment' ? (
        <p className={styles.thanks}>
          Thank you! Your order is in. We sent you a confirmation email.
        </p>
      ) : null}
      <div className="cbSectionHead">
        <div>
          <h2 className={styles.title}>{order.title}</h2>
          <p className="cbMuted">
            Order #{order.id}
            {order.paid_at ? `, placed ${date(order.paid_at)}` : ''}
          </p>
        </div>
        <StatusPill status={order.status} />
      </div>
      <Progress status={order.status} />
      {order.status === 'pending_payment' ? (
        <p className="cbMuted">
          Checking your payment… If you closed the payment page, nothing was charged.
        </p>
      ) : null}
      {order.tracking_url ? (
        <p>
          <a
            className={`cbNextButton ${styles.track}`}
            href={order.tracking_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            Track your package
          </a>
        </p>
      ) : null}

      <div className={styles.grid}>
        <div>
          <p className={styles.label}>Your book</p>
          <p>
            {order.product_name}, {order.size_label || '8.5 x 8.5 in'}, {order.page_count} pages
            <br />
            {order.quantity} {order.quantity === 1 ? 'copy' : 'copies'}
          </p>
        </div>
        <div>
          <p className={styles.label}>{order.is_gift ? 'A gift for' : 'Shipping to'}</p>
          <p>
            {a.name}
            <br />
            {a.street1}
            {a.street2 ? `, ${a.street2}` : ''}
            <br />
            {a.city}, {a.state_code} {a.postcode}
          </p>
        </div>
        <div>
          <p className={styles.label}>Paid</p>
          <p>
            Book {dollars(order.price_book)}
            <br />
            Shipping {dollars(order.price_shipping)}
            {order.voucher ? (
              <>
                <br />
                Gift voucher −{dollars(order.voucher)}
              </>
            ) : null}
            {order.tax ? (
              <>
                <br />
                Tax {dollars(order.tax)}
              </>
            ) : null}
            {order.total !== null ? (
              <>
                <br />
                <b>Total {dollars(order.total)}</b>
              </>
            ) : null}
          </p>
        </div>
      </div>

      {order.can_cancel ? (
        <div className={styles.cancelBox}>
          <p>
            You can cancel for a full refund until{' '}
            <b>{moment(order.cancel_until).format('h:mm a')}</b>. After that, your book goes to the
            printer.
          </p>
          <button type="button" className={styles.cancel} onClick={cancel} disabled={cancelling}>
            {cancelling ? 'Cancelling…' : 'Cancel and refund'}
          </button>
        </div>
      ) : null}
      {error ? <p className="cbError">{error}</p> : null}
      <p className="cbMuted">
        Questions? Email{' '}
        <a className="cbTextLink" href="mailto:info@getmorsel.com">
          info@getmorsel.com
        </a>{' '}
        with your order number.
      </p>
    </section>
  )
}

// Books they asked for with "Request my book": saved, waiting for the Morsel team's email
const RequestList = ({ requests }) => (
  <section className={styles.requests}>
    <h2 className="cbSectionTitle">Requested books</h2>
    <p className="cbHint">
      Your book is saved. We’ll email you within 1 business day of your request to finish your order
      and take payment.
    </p>
    <div className={styles.list}>
      {requests.map((q) => (
        <Link
          key={q.id}
          to={routes.purchaseBook}
          state={{
            currentStep: STEPS_ORDER_BOOK['CHECKOUT_STEP'],
            groupId: q.group_id,
            orderId: q.cookbook_id,
          }}
          className={styles.row}
        >
          <span>
            <b>{q.title || 'Your cookbook'}</b>
            <small>
              Requested {date(q.updated_at || q.created_at)}, {q.quantity}{' '}
              {q.quantity === 1 ? 'copy' : 'copies'}
              {q.due !== null && q.due !== undefined ? `, about ${dollars(q.due)}` : ''}
            </small>
          </span>
          <span className={`${styles.pill} ${styles.pill_busy}`}>
            {REQUEST_STATUS_TEXT[q.status] || 'Requested'}
          </span>
        </Link>
      ))}
    </div>
  </section>
)

const OrderList = () => {
  const [orders, setOrders] = useState(null)
  const [requests, setRequests] = useState([])
  useEffect(() => {
    getCookbookOrders()
      .then(({ data }) => setOrders(data))
      .catch(() => setOrders([]))
    getCookbookOrderRequests()
      .then(({ data }) => setRequests(data || []))
      .catch(() => setRequests([]))
  }, [])

  if (!orders) return <p className="cbMuted">Loading…</p>
  if (!orders.length && requests.length) return <RequestList requests={requests} />
  if (!orders.length) {
    return (
      <section className={`cbSection ${styles.empty}`}>
        <h2 className="cbSectionTitle">No cookbook orders yet</h2>
        <p className="cbHint">
          Turn your family’s recipes into a printed book. It only takes a few minutes.
        </p>
        <Link className="cbNextButton" to={routes.purchaseBook}>
          Make a cookbook
        </Link>
      </section>
    )
  }
  return (
    <>
      {requests.length ? <RequestList requests={requests} /> : null}
      <div className={styles.list}>
        {orders.map((o) => (
          <Link key={o.id} to={`${routes.cookbookOrders}/${o.id}`} className={styles.row}>
            <span>
              <b>{o.title}</b>
              <small>
                Order #{o.id}, {date(o.paid_at || o.created_at)}, {o.quantity}{' '}
                {o.quantity === 1 ? 'copy' : 'copies'}
              </small>
            </span>
            <StatusPill status={o.status} />
          </Link>
        ))}
      </div>
    </>
  )
}

const CookbookOrders = () => {
  const { orderId } = useParams()
  return (
    <Layout title="Your cookbook orders">
      <div className={`cbPage ${styles.page}`}>
        <div className="cbIntro">
          <div>
            {orderId ? (
              <Link className="cbBack" to={routes.cookbookOrders}>
                <ArrowBack />
                All orders
              </Link>
            ) : null}
            <h1 className="cbTitle">Your cookbook orders</h1>
          </div>
          <Link className="cbIntroLink" to={routes.purchaseBook}>
            Make a cookbook
          </Link>
        </div>
        {orderId ? <OrderDetail id={orderId} /> : <OrderList />}
      </div>
    </Layout>
  )
}

export default CookbookOrders
